// // /////////////////////////////////////////////////////////////////////////////////////
// //                              Copyright (c) 2021 - 2025
// //                                    RXO Inc.
// //                          All Rights Reserved Worldwide
// //
// // WARNING:  This program (or document) is unpublished, proprietary
// // property of RXO Inc. and is to be maintained in strict confidence.
// // Unauthorized reproduction, distribution or disclosure of this program
// // (or document), or any program (or document) derived from it is
// // prohibited by State and Federal law, and by local law outside of the U.S.
// // /////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { sumBy, uniq } from "lodash";

import AnalyticsSummary from "./AnalyticsSummary";
import getCurrency from "../../../helpers/getCurrency";
import {
  fetchDatRates,
  fetchLoadHistory
} from "../../../slices/analyticsSlice";
import PanelStateIndicator from "../PanelStateIndicator";
import messages from "../../../helpers/messages";
import { NoMatches } from "../lane-match/NoMatches";
import { round } from "../../../helpers/laneCalculations";
import getInboundOutboundRatio from "../../../helpers/getInboundOutboundRatio";
import AnalyticsFiltersWithSlider from "./AnalyticsFiltersWithSlider";
import GraphsSection from "./GraphsSection";
import RankBreakdown from "./RankBreakdown";
import { DATRateViewDrawer } from "./DATRateViewDrawer";
import { DATRateViewButton } from "./DATRateViewButton";
import { division as divisionType } from "../../../helpers/types";

export const Analytics = ({ userDivision, onMount }) => {
  useEffect(onMount, [onMount]);
  const [openDrawer, setOpenDrawer] = useState(false);

  const {
    bidSummary: { summary },
    analytics: {
      loads,
      isFetching,
      isFailure,
      filters: analyticsFilters,
      inbound,
      outbound
    },
    bidPricing: { lanesLoading, lanesError }
  } = useSelector(state => state);
  const dispatch = useDispatch();
  const bidDivision = summary?.division;
  const division = bidDivision ?? userDivision;

  const currency = getCurrency(division);
  const loading = lanesLoading || isFetching;
  const error = isFailure || lanesError;
  const includedLoads = loads?.filter(l => !l.exclude);
  let showViewDATRateViewButton = userDivision === divisionType.coy;

  if (bidDivision && bidDivision !== divisionType.coy) {
    showViewDATRateViewButton = false;
  }

  return (
    <div
      style={{ height: 376 }}
      className="bg-white analytics"
      data-testid="analytics"
    >
      {<AnalyticsFiltersWithSlider division={division} />}

      {(error || loading) && loads === null && (
        <div className="box panel">
          <PanelStateIndicator
            iconSize="32px"
            loading={loading}
            error={error}
            errorMessage={
              messages.sorrySomethingWentWrongPleaseTryAgain.defaultMessage
            }
            handleRefresh={() =>
              dispatch(
                fetchLoadHistory({
                  division,
                  analyticsFilters
                })
              )
            }
          />
        </div>
      )}

      {loads?.length > 0 && (
        <>
          <div className="grid__cell t15 border-right-grey analytics-summary text-center">
            <AnalyticsSummary
              currency={currency}
              inboundToOutboundRatio={getInboundOutboundRatio(
                inbound,
                outbound
              )}
              volumeToCarrierRatio={`${includedLoads.length}:${
                uniq(includedLoads.map(l => l.carrierId)).length
              }`}
              averageCost={
                includedLoads.length &&
                round(sumBy(includedLoads, "cost") / includedLoads.length)
              }
              averageCPM={
                includedLoads.length &&
                round(
                  sumBy(includedLoads, "cost") /
                    sumBy(includedLoads, l => l.miles || 1)
                )
              }
            />
          </div>
          {
            <>
              <div className="grid__cell t15 rank-breakdown">
                <RankBreakdown currency={currency} division={division} />
              </div>
              <div className="grid__cell t70 border-left-grey graph-section">
                <GraphsSection currency={currency} division={division} />
              </div>
            </>
          }
        </>
      )}
      {loads?.length === 0 && !loading && !error && (
        <NoMatches
          heading={messages.noResults.defaultMessage}
          message={messages.noLaneDataMessage.defaultMessage}
          button={
            showViewDATRateViewButton ? (
              <DATRateViewButton
                onClick={() => {
                  setOpenDrawer(true);
                  dispatch(fetchDatRates());
                }}
                className="mt-16"
              />
            ) : null
          }
        />
      )}
      {loads === null && !loading && !error && (
        <NoMatches
          heading={messages.getStartedWithPricingAnalytics.defaultMessage}
          message={messages.pleaseFillOutTheSearchCriteria.defaultMessage}
          optional={messages.requiredAnalyticsFieldsMessage.defaultMessage}
        />
      )}
      <DATRateViewDrawer
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
      />
    </div>
  );
};

export default Analytics;
